import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { yup } from "utils";

import { useRedirect } from "data/hub/hooks";

import { useFeatures } from "components/FeatureFlag";

import { useTemplateDraft, useTemplatePublish } from ".";

const validationSchema = yup.object().shape({
  gated: yup.boolean(),
  public: yup.boolean().when("gated", {
    is: false,
    then: yup
      .boolean()
      .oneOf([false], "Template can not be public when it is not gated"),
  }),
});

export function useTemplatePublishForm(process, onDone, onClose) {
  const currentTemplate = process?.template?.current;
  const redirect = useRedirect();
  const allowTemplatesMarketplace = useFeatures("add-to-hub");

  const formProps = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      isGated: currentTemplate?.gated || false,
      isPublic: currentTemplate?.public || false,
    },
  });

  const {
    setValue,
    watch,
    formState: { isDirty },
  } = formProps;

  const [watchIsGated, watchIsPublic] = watch(["isGated", "isPublic"]);

  useEffect(() => {
    if (isDirty) {
      setValue("isPublic", watchIsGated);
    }
  }, [watchIsGated, isDirty, setValue]);

  const templateDraft = useTemplateDraft();

  const { publishTemplate } = useTemplatePublish(
    useCallback(() => {
      if (allowTemplatesMarketplace) redirect("templates/all");
      onDone();
    }, [onDone, allowTemplatesMarketplace, redirect])
  );

  const handleOnSubmit = async ({ isGated, isPublic }) => {
    const draft = await templateDraft.getOrCreateDraft(process?.id);
    const published = await publishTemplate(draft?.id, isGated, isPublic);

    if (draft && published) {
      onClose();
    }
  };

  return {
    formProps: formProps,
    submit: handleOnSubmit,
    isSubmitting: templateDraft.loading,
    isGated: watchIsGated === true,
    isPublic: watchIsPublic === true,
  };
}

export default useTemplatePublishForm;
